import { useEffect, useRef } from "preact/hooks";
import type { App } from "../app.ts";
import { formatDisplayDate } from "../lib/format.ts";
import { groupDocs } from "../lib/group.ts";
import { useAppState } from "./hooks.ts";
import { NewForm, type NewMode } from "./new-form.tsx";

interface Props {
  app: App;
  newMode: NewMode;
  setNewMode: (mode: NewMode) => void;
}

export function Sidebar({ app, newMode, setNewMode }: Props) {
  const s = useAppState(app);
  const search = useRef<HTMLInputElement>(null);
  const files = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (s.sidebarOpen && !newMode) search.current?.focus();
  }, [s.sidebarOpen]);

  const byPath = new Map(s.docs.map((d) => [d.path, d]));
  const shown = s.results
    ? s.results.map((p) => byPath.get(p)).filter((d) => d !== undefined)
    : s.docs;
  const groups = s.results
    ? [
        {
          id: "results",
          label: `${shown.length} ${shown.length === 1 ? "result" : "results"}`,
          items: shown,
        },
      ]
    : groupDocs(shown);

  return (
    <aside
      id="sidebar"
      class="sidebar"
      aria-label="Files"
      hidden={!s.sidebarOpen}
      inert={!s.sidebarOpen}
    >
      <div class="sidebar-head">
        <label class="visually-hidden" for="search">
          Search
        </label>
        <input
          id="search"
          ref={search}
          class="field"
          type="search"
          placeholder="Search titles and text"
          autocomplete="off"
          value={s.query}
          onInput={(e) => app.setQuery(e.currentTarget.value)}
        />
      </div>

      <div class="new-area">
        {newMode ? (
          <NewForm app={app} mode={newMode} done={() => setNewMode(null)} />
        ) : (
          <>
            <button class="button" type="button" onClick={() => setNewMode("thought")}>
              New thought
            </button>
            <button class="button" type="button" onClick={() => setNewMode("document")}>
              New document
            </button>
          </>
        )}
      </div>

      <div class="doc-list">
        {s.docs.length === 0 ? (
          <p class="muted pad">No files yet. Start a thought, or import some Markdown.</p>
        ) : s.results && shown.length === 0 ? (
          <p class="muted pad">Nothing matches “{s.query}”.</p>
        ) : (
          groups.map((g) => (
            <section class="doc-group" key={g.id}>
              <h2 class="group-label">{g.label}</h2>
              <ul class="doc-items">
                {g.items.map((d) => (
                  <li key={d.path}>
                    <button
                      type="button"
                      class="doc-item"
                      aria-current={s.current?.path === d.path ? "true" : undefined}
                      onClick={async () => {
                        await app.open(d.path);
                        if (app.state.get().current?.path === d.path) app.toggleSidebar(false);
                      }}
                    >
                      <span class="doc-title">{d.title || d.path}</span>
                      <span class="doc-meta">
                        {[d.date && formatDisplayDate(d.date), d.title ? d.path : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>

      <div class="sidebar-foot">
        {s.unusedImages.length > 0 && (
          <div class="unused-images">
            <p class="source-line">
              {s.unusedImages.length === 1
                ? "1 picture is not used by any note."
                : `${s.unusedImages.length} pictures are not used by any note.`}
            </p>
            <button class="button" type="button" onClick={() => app.reviewUnusedImages()}>
              Review pictures
            </button>
          </div>
        )}
        <p class="source-line">
          {s.storeKind === "fsa" ? `Folder: ${s.storeLabel}` : "Stored in this browser"}
          {s.storeKind !== "fsa" &&
            s.persisted === false &&
            ". The browser may clear it, so Export keeps copies."}
        </p>
        <div class="source-actions">
          {s.canPickFolder && (
            <button class="button" type="button" onClick={() => app.pickFolder()}>
              {s.storeKind === "fsa" ? "Change folder" : "Open a folder"}
            </button>
          )}
          {s.storeKind === "fsa" && (
            <button class="button" type="button" onClick={() => app.useBrowserLibrary()}>
              Use browser library
            </button>
          )}
          <button class="button" type="button" onClick={() => files.current?.click()}>
            Import files
          </button>
          {!s.canPickFolder && (
            <p class="muted small">Files stay in this browser. Export them to keep a copy.</p>
          )}
        </div>
        <input
          ref={files}
          type="file"
          accept=".md,.markdown,.txt"
          multiple
          hidden
          onChange={(e) => {
            app.importFiles(e.currentTarget.files ?? []);
            e.currentTarget.value = "";
          }}
        />
      </div>
    </aside>
  );
}
